/**
 * Custom hook for handling advertisement impression and click tracking
 * Uses IntersectionObserver API for automatic impression tracking
 */

import { useCallback, useEffect, useRef, useState } from "react";
import AdvertisingService from "@/services/advertising";
import type { AdPlacement } from "@/types/advertising";

interface UseAdTrackingOptions {
  adId: string;
  placement: AdPlacement;
  targetUrl?: string;
  threshold?: number; // Percentage of ad that must be visible (0-1)
  minVisibilityTime?: number; // Minimum time in ms ad must be visible
}

interface UseAdTrackingReturn {
  adRef: React.RefObject<HTMLDivElement | null>;
  handleClick: (event: React.MouseEvent) => void;
  isTracked: boolean;
  impressionTracked: boolean;
}

/**
 * useAdTracking hook
 * Provides automatic impression tracking via IntersectionObserver and click tracking
 *
 * @param options - Tracking configuration options
 * @returns Object with ad ref, click handler, and tracking status
 */
export const useAdTracking = ({
  adId,
  placement,
  targetUrl,
  threshold = 0.5,
  minVisibilityTime = 1000,
}: UseAdTrackingOptions): UseAdTrackingReturn => {
  const adRef = useRef<HTMLDivElement>(null);
  const [impressionTracked, setImpressionTracked] = useState(false);
  const [isTracked, setIsTracked] = useState(false);
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasBeenVisibleRef = useRef(false);

  /**
   * Track impression when ad becomes visible for required duration
   */
  const trackImpression = useCallback(async () => {
    if (impressionTracked || hasBeenVisibleRef.current) {
      return;
    }

    hasBeenVisibleRef.current = true;

    try {
      const impressionData = {
        adId,
        placement,
        pageUrl: typeof window !== "undefined" ? window.location.href : "",
        referrer:
          typeof document !== "undefined" ? document.referrer : undefined,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        deviceType: getDeviceType(),
      };

      await AdvertisingService.TrackImpression(impressionData);
      setImpressionTracked(true);
      setIsTracked(true);
    } catch (error) {
      console.error("Failed to track ad impression:", error);
    }
  }, [adId, placement, impressionTracked]);

  /**
   * Handle ad click tracking
   */
  const handleClick = useCallback(
    async (event: React.MouseEvent) => {
      try {
        const clickData = {
          adId,
          placement,
          pageUrl: typeof window !== "undefined" ? window.location.href : "",
          targetUrl: targetUrl || "",
          referrer:
            typeof document !== "undefined" ? document.referrer : undefined,
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          deviceType: getDeviceType(),
        };

        // Track click asynchronously without blocking navigation
        AdvertisingService.TrackClick(clickData).catch((error) => {
          console.error("Failed to track ad click:", error);
        });
      } catch (error) {
        console.error("Error in click handler:", error);
      }
    },
    [adId, placement, targetUrl],
  );

  /**
   * Set up IntersectionObserver for impression tracking
   */
  useEffect(() => {
    if (typeof window === "undefined" || !adRef.current || impressionTracked) {
      return;
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          // Ad is visible at required threshold
          if (!visibilityTimerRef.current) {
            visibilityTimerRef.current = setTimeout(() => {
              trackImpression();
            }, minVisibilityTime);
          }
        } else {
          // Ad is no longer visible, clear timer
          if (visibilityTimerRef.current) {
            clearTimeout(visibilityTimerRef.current);
            visibilityTimerRef.current = null;
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: [0, threshold, 1],
      rootMargin: "0px",
    });

    observer.observe(adRef.current);

    return () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
      observer.disconnect();
    };
  }, [
    adId,
    placement,
    threshold,
    minVisibilityTime,
    impressionTracked,
    trackImpression,
  ]);

  return {
    adRef,
    handleClick,
    isTracked,
    impressionTracked,
  };
};

/**
 * Helper function to determine device type
 */
function getDeviceType(): "desktop" | "mobile" | "tablet" {
  if (typeof window === "undefined") {
    return "desktop";
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /mobile|android|iphone|ipod|blackberry|windows phone/.test(
    userAgent,
  );
  const isTablet = /tablet|ipad/.test(userAgent);

  if (isTablet) {
    return "tablet";
  } else if (isMobile) {
    return "mobile";
  } else {
    return "desktop";
  }
}

export default useAdTracking;
